import { CheckOutlined, DownOutlined } from '@ant-design/icons'
import { Dropdown, DropdownProps, Flex, MenuProps, Typography } from 'antd'
import i18n from 'i18next'
import React from 'react'
import { useTranslation } from 'react-i18next'
import { storeDefaultLocale, useAuth } from '../../auth'
import { updateUser } from '../api'
import { EN_US, Locale, NL_NL, locales } from '../locale'

const flags: Record<Locale, string> = {
    [EN_US]: '🇬🇧',
    [NL_NL]: '🇳🇱',
}

const shortLabels: Record<Locale, string> = {
    [EN_US]: 'EN',
    [NL_NL]: 'NL',
}

export type LocaleSwitchProps = Omit<DropdownProps, 'menu' | 'children'>

export function LocaleSwitch(props: LocaleSwitchProps) {
    const { t, i18n: instance } = useTranslation()
    const { user } = useAuth()

    const current = (locales.find((locale) => locale === instance.language) ?? locales[0]) as Locale

    function changeLocale(locale: Locale) {
        if (locale === current) {
            return
        }

        i18n.changeLanguage(locale)
        storeDefaultLocale(locale)

        if (user) {
            updateUser(user.id, { locale }).catch(() => {})
        }
    }

    const items: MenuProps['items'] = locales.map((locale) => {
        return {
            key: locale,
            onClick: () => changeLocale(locale),
            label: (
                <Flex
                    align="center"
                    gap="small"
                    className="min-w-36"
                >
                    <span className="text-base leading-none">{flags[locale]}</span>
                    <span className="flex-1">{t(`locales.${locale}`)}</span>
                    {locale === current && <CheckOutlined className="text-primary" />}
                </Flex>
            ),
        }
    })

    return (
        <Dropdown
            trigger={['click']}
            menu={{ items, selectedKeys: [current] }}
            {...props}
        >
            <button
                type="button"
                className="flex items-center gap-1.5 rounded-full px-2 py-1.5 text-sm text-ink-tertiary transition-colors hover:bg-black/[0.04] hover:text-ink-secondary"
            >
                <span className="text-base leading-none">{flags[current]}</span>
                <Typography.Text className="font-medium !text-inherit">{shortLabels[current]}</Typography.Text>
                <DownOutlined className="text-[10px]" />
            </button>
        </Dropdown>
    )
}
